import type { TButtonMenuOption } from "components/shared/ButtonOptionsMenu";
import ButtonOptionsMenu from "components/shared/ButtonOptionsMenu";
import InlineButton from "components/shared/InlineButton";
import { PortalType } from "contexts/portal.context";
import { SmallBodyHighlight, TinyBody } from "helpers/TypographyComponents";
import { getLocalStorageValue, updateLocalStorage } from "hooks/useLocalStorageListener";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { TableType } from "./utils";
import { getColumnViewOptions, mergeColumnViewWithRequiredOptions } from "./utils";

interface IViewColumnOptions {
    tableType: TableType;
}
/**
 * Render button with popover options to select columns to show
 */
const ViewColumnOptions = ({ tableType }: IViewColumnOptions) => {
    const [t] = useTranslation();
    const portalType = useContext(PortalType);
    const defaultOptions = useMemo(() => getColumnViewOptions(tableType, portalType), [tableType, portalType]);

    const initialChosenOptions = mergeColumnViewWithRequiredOptions(
        getLocalStorageValue<TButtonMenuOption[] | null>(`column_view:${tableType}`),
        defaultOptions,
    );

    const [chosenOptions, setChosenOptions] = useState<TButtonMenuOption[]>(initialChosenOptions);

    const handleChooseOption = (option: TButtonMenuOption) => {
        if (option.disabled) return;

        const isSelected = chosenOptions?.some((opt) => opt?.id === option.id);
        if (isSelected) {
            setChosenOptions((prev) => [...prev].filter((opt) => opt.id !== option.id));
        } else {
            setChosenOptions((prev) => [...prev, option]);
        }
    };

    const handleResetOptions = () => {
        setChosenOptions(defaultOptions);
    };

    // Save in local storage (required columns are always persisted)
    useEffect(() => {
        const value = mergeColumnViewWithRequiredOptions(chosenOptions, defaultOptions);
        updateLocalStorage({ key: `column_view:${tableType}`, value: JSON.stringify(value), withEvent: true });
    }, [chosenOptions, defaultOptions, tableType]);

    const MenuHeader = () => {
        return (
            <div style={{ paddingBlock: "0.4rem", paddingInline: "0.75rem" }}>
                <div style={{ display: "flex", gap: "1.5rem" }}>
                    <SmallBodyHighlight>{t("Management.CustomizeDisplay")}</SmallBodyHighlight>
                    <InlineButton size="small" hideIcon onClick={handleResetOptions}>
                        {t("Management.Reset")}
                    </InlineButton>
                </div>
                <TinyBody>{t("Management.ColumnsCount", { count: defaultOptions.length })}</TinyBody>
            </div>
        );
    };

    return (
        <ButtonOptionsMenu
            optBtnType="switch"
            label={t("Management.Display")}
            menuHeader={<MenuHeader />}
            handleChooseOption={handleChooseOption}
            disabledTooltip={t("Management.EssentialColumn")}
            options={defaultOptions}
            chosenOptions={chosenOptions}
        />
    );
};

export default ViewColumnOptions;
